import { Component, Element, h, Listen, Prop, State } from '@stencil/core';
import { createFocusTrap } from 'focus-trap';
import {
    CookieWallContent,
    CookieWallModal,
    CookieWallSettings,
} from './models/cookie-wall.models';
import { SetPermissionEvent } from './models/set-permission-event';
import {
    getCookieSettings,
    getCookieWidgetContent,
    hasCookie,
    isOldCookie,
    reformatOldCookie,
    saveCookieSettings,
} from './pnl-cookie-widget.service';

@Component({
    tag: 'pnl-cookie-wall-widget',
    shadow: true,
    styleUrl: './pnl-cookie-wall.scss',
})
export class PnlCookieWall {
    private initialized = false;
    private focusTrap;

    @State() visible = false;
    @State() showModalType: CookieWallModal = CookieWallModal.Overview;
    @State() content: CookieWallContent;
    @State() settings: CookieWallSettings;

    @Element() component: HTMLElement;

    /** Comma-separated list of available locales that the user can choose from in the pnl-language-selector. */
    @Prop() availableLocales = 'nl_NL';

    /** The locale the menu is rendered in. */
    @Prop({ mutable: true, reflect: true }) locale = 'nl_NL';

    /**
     * Set the domain whose cookie-wall content will be loaded.
     * Uses window.location.hostname if not specified;
     */
    @Prop() domain: string = undefined;

    private body = document.getElementsByTagName('body')[0];

    @Listen('onLocaleChanged')
    async changeLocale(language: CustomEvent) {
        this.locale = this.getLocale(language.detail);
        this.content = await this.loadContent();
        this.settings = getCookieSettings(
            this.content.cookieDomain,
            this.content.cookieSettingsPage.categories
        );
    }

    private getLocale(language: string) {
        const locales = this.availableLocales.split(',');
        return locales.find((l) => l.startsWith(language)) || this.locale;
    }

    @Listen('postnl:cookiewall:show', { target: 'window' })
    async showCookieWall() {
        if (!this.content) {
            this.content = await this.loadContent();
        }

        this.settings = getCookieSettings(
            this.content.cookieDomain,
            this.content.cookieSettingsPage.categories
        );

        this.visible = true;

        // Make sure the body is not scrollable when the modal is open
        Object.assign(this.body.style, {
            position: 'fixed',
            overflow: 'hidden',
            width: '100vw',
            height: '100vh',
        });
    }

    @Listen('postnl:cookiewall:showsettings', { target: 'window' })
    async showCookieWallSettings() {
        this.showModalType = CookieWallModal.Settings;
        await this.showCookieWall();
    }

    private async loadContent(): Promise<CookieWallContent> {
        return await getCookieWidgetContent(this.domain, this.locale);
    }

    @Listen('postnl:cookiewall:close', { target: 'window' })
    close() {
        this.showModalType = CookieWallModal.Overview;
        this.visible = false;

        this.focusTrap.deactivate();
        this.focusTrap = null;

        // Remove the style that makes the body scrollable
        this.body.removeAttribute('style');
    }

    @Listen('postnl:cookiewall:setPermission', { target: 'window' })
    async setPermission(event : CustomEvent<SetPermissionEvent>) {
        
        this.settings = getCookieSettings(
            this.content.cookieDomain,
            this.content.cookieSettingsPage.categories
        );

        var category = this.settings.categories.find(c => c.category == event.detail.category);
        if (category) {
            category.checked = event.detail.allow;
            saveCookieSettings(this.settings);
        }
    }

    async componentDidLoad() {
        const PNL_SESSION_READY_TIMEOUT = 1300;

        const sessionReady = setTimeout(async () => {
            console.debug('Did not receive pnl_session_ready event.');
            await this.activate();
        }, PNL_SESSION_READY_TIMEOUT);

        document.body.addEventListener('pnl_session_ready', async () => {
            console.debug('Received pnl_session_ready event.');
            clearTimeout(sessionReady);
            await this.activate();
        });
    }

    private async activate() {
        if (this.initialized) return;

        this.initialized = true;
        if (hasCookie()) {
            await this.fixOldCookie();
            console.debug(
                'Permission info cookie found. No need to display cookie widget.'
            );
            return;
        }

        this.content = await this.loadContent();

        const currentPage = window.location.pathname;
        if (this.content.blackListedPages.includes(currentPage)) {
            console.debug(
                'No permission info cookie found, but page is black-listed. Not displaying cookie widget.'
            );
            return;
        }

        console.debug(
            'No permission info cookie found. Display cookie widget.'
        );
        this.showCookieWall();
    }

    private async fixOldCookie() {
        if (isOldCookie()) {
            console.info(
                'Overwriting deprecated cookie format with new format'
            );
            const settings = await this.loadContent();
            reformatOldCookie(settings);
        }
    }

    render() {
        return this.visible ? this.renderPopup() : undefined;
    }

    renderPopup() {
        return (
            <div class="pnl-cookie-wall" id="cookiewall">
                {this.showModalType === CookieWallModal.Overview ? (
                    <pnl-cookie-widget-overview
                        content={this.content.landingPage}
                        locale={this.locale}
                        availableLocales={this.availableLocales}
                        settings={this.settings}
                    ></pnl-cookie-widget-overview>
                ) : (
                    <pnl-cookie-widget-settings
                        content={this.content.cookieSettingsPage}
                        locale={this.locale}
                        settings={this.settings}
                    ></pnl-cookie-widget-settings>
                )}
            </div>
        );
    }

    @Listen('settingsSelected')
    openSettings() {
        this.showModalType = CookieWallModal.Settings;
    }

    @Listen('cancelled')
    cancel() {
        if (this.showModalType === CookieWallModal.Overview) {
            this.close();
        } else {
            this.showModalType = CookieWallModal.Overview;
        }
    }

    @Listen('saved')
    saveSettings() {
        saveCookieSettings(this.settings);
        this.close();
    }

    @Listen('keydown', { target: 'window' })
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && this.focusTrap) {
            this.close();
        }
    }

    componentDidRender() {
        const cookiewallElement = this.component.shadowRoot.querySelector(
            '#cookiewall'
        ) as HTMLElement;

        if (cookiewallElement && !this.focusTrap) {
            this.focusTrap = createFocusTrap(cookiewallElement, {
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                tabbableOptions: {
                    getShadowRoot: true,
                },
            });

            this.focusTrap.activate();
        }
    }
}
